import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { PublicationItem, PublicSite } from '../../shared/api/types';
import { useAsync } from '../../shared/ui/useAsync';
import { formatWhen } from '../events/shared';
import { countryName } from '../../shared/geo/places';
import { LiveBadge, LivePlayer, useLiveState } from '../live/live';
import { youtubeThumb } from './youtube';
import { joinPath } from '../churches/JoinByInvitePage';
import { Icon } from '../shell/nav';

type PublicEventItem = { kind: 'event' | 'meeting'; title: string; startsAt: string; endsAt: string; placeName: string | null; token: string };

const KIND_LABEL: Record<string, string> = { sermon: 'compartió una predicación', study: 'compartió un estudio', article: 'publicó un artículo' };

/** «hace 3 días», «hace 2 horas»… */
export function timeAgo(iso: string, now = Date.now()): string {
  const diff = (new Date(iso).getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
  for (const [unit, secs] of steps) if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  return 'justo ahora';
}

function Avatar({ site, size = 'small' }: { site: PublicSite; size?: 'small' | 'big' }) {
  const cls = `wall-avatar wall-avatar--${size}`;
  return site.logoUrl ? (
    <img className={cls} src={site.logoUrl} alt={size === 'big' ? `Logo de ${site.name}` : ''} />
  ) : (
    <span className={`${cls} wall-avatar--initial`} aria-hidden={size !== 'big'}>
      {site.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/** Una tarjeta del muro con la cabecera de la iglesia (logo, nombre, cuándo). */
function Post({ site, what, when, badge, children }: { site: PublicSite; what?: string; when?: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <article className="wall-post">
      <header className="wall-post__head">
        <Avatar site={site} />
        <div className="wall-post__who">
          <p>
            <strong>{site.name}</strong>
            {what ? <span className="muted"> {what}</span> : null}
          </p>
          {when || badge ? (
            <p className="small muted">
              {badge}
              {badge && when ? ' · ' : null}
              {when}
            </p>
          ) : null}
        </div>
      </header>
      <div className="wall-post__body">{children}</div>
    </article>
  );
}

function Carousel({ slides }: { slides: PublicSite['slides'] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);
  const s = slides[i];
  if (!s) return null;
  const external = s.buttonUrl?.startsWith('https://');
  return (
    <section className="hero" aria-label="Destacados" style={{ backgroundImage: `url(${s.imageUrl})` }}>
      <div className="hero__shade">
        {s.title ? <h2>{s.title}</h2> : null}
        {s.description ? <p>{s.description}</p> : null}
        {s.buttonText && s.buttonUrl ? (
          external ? (
            <a className="btn btn--primary" href={s.buttonUrl} target="_blank" rel="noopener noreferrer">
              {s.buttonText}
            </a>
          ) : (
            <Link className="btn btn--primary" to={s.buttonUrl}>
              {s.buttonText}
            </Link>
          )
        ) : null}
      </div>
      {slides.length > 1 ? (
        <div className="hero__dots">
          {slides.map((x, n) => (
            <button key={x.id} type="button" aria-label={`Destacado ${n + 1}`} aria-current={n === i} onClick={() => setI(n)} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Copia el enlace de la iglesia para pegarlo donde sea. */
/** Compartir la página: el menú del celular si lo hay; si no, WhatsApp o copiar el enlace. */
function ShareChurch({ url, name }: { url: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  async function share() {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: name, text: `Conoce ${name}`, url });
        return;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
      }
    }
    setOpen((v) => !v);
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // El navegador no lo permite.
    }
  }
  return (
    <>
      <button type="button" className="church-action" aria-expanded={open} onClick={() => void share()}>
        <Icon name="share" /> Compartir
      </button>
      {open ? (
        <span className="church-actions__share">
          <a className="church-action" href={`https://wa.me/?text=${encodeURIComponent(`Conoce ${name}: ${url}`)}`} target="_blank" rel="noopener noreferrer">
            Por WhatsApp
          </a>
          <button type="button" className="church-action" onClick={() => void copy()}>
            {copied ? 'Enlace copiado' : 'Copiar enlace'}
          </button>
        </span>
      ) : null}
    </>
  );
}

/** Página pública de la iglesia, estilo muro: portada, datos a un lado y publicaciones al centro. */
export function PublicChurchSite({ slug, site, feed }: { slug: string; site: PublicSite; feed: PublicationItem[] }) {
  const events = useAsync((signal) => api.get<{ items: PublicEventItem[] }>(`/calendar/public/churches/${slug}/events`, signal).then((r) => r.items).catch(() => [] as PublicEventItem[]), [slug]);
  const live = useLiveState(`/multichurch/public/${slug}/live`);
  const posts = [...feed].sort((a, b) => b.publishAt.localeCompare(a.publishAt));
  const upcoming = events.status === 'ready' ? events.data.slice(0, 4) : [];
  const where = [site.address, site.city, site.state, site.country ? countryName(site.country) : null].filter(Boolean).join(', ');
  const place = [site.city, site.state].filter(Boolean).join(', ');
  const wa = site.whatsapp ? `https://wa.me/${site.whatsapp.replace(/\D/g, '').replace(/^(\d{10})$/, '52$1')}` : null;
  const style = site.primaryColor ? ({ '--color-brand': site.primaryColor } as CSSProperties) : undefined;
  const cover = site.slides[0]?.imageUrl;
  const pageUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mykeora.com';
  const socials = [
    site.facebookUrl ? { href: site.facebookUrl, label: 'Facebook' } : null,
    site.instagramUrl ? { href: site.instagramUrl, label: 'Instagram' } : null,
    site.youtubeUrl ? { href: site.youtubeUrl, label: 'YouTube' } : null,
    site.website ? { href: site.website, label: 'Sitio web' } : null,
  ].filter((x): x is { href: string; label: string } => x !== null);

  const welcome = (
    <Post site={site} badge="Bienvenida">
      <p className="wall-post__title">¡Bienvenido a {site.name}!</p>
      <p>
        {site.about ? `${site.about.trim().replace(/[.\s]+$/, '')}. ` : ''}
        {site.schedules.length ? `Te esperamos: ${site.schedules.map((s) => `${s.label}, ${s.day} ${s.time}`).join(' · ')}.` : 'Nos encantará conocerte.'}
      </p>
      <div className="actions actions--wrap">
        <Link className="btn btn--secondary" to={`/visita/${slug}`}>
          Quiero visitarlos
        </Link>
      </div>
    </Post>
  );

  return (
    <div className="wall" style={style}>
      <header className="wall-head">
        <div className="wall-cover" style={cover ? { backgroundImage: `url(${cover})` } : undefined} aria-hidden="true" />
        <div className="wall-head__main">
          <Avatar site={site} size="big" />
          <div className="wall-head__name">
            <h1>{site.name}</h1>
            {site.tagline ? <p className="wall-head__tagline">{site.tagline}</p> : null}
            <p className="small muted">{[site.denomination, place || (site.country ? countryName(site.country) : null)].filter(Boolean).join(' · ')}</p>
          </div>
          <div className="church-actions wall-head__actions">
            <div className="church-actions__main">
              {live?.status === 'live' ? (
                <a className="btn btn--primary" href="#en-vivo">
                  ● Ver en vivo
                </a>
              ) : null}
              <Link className="btn btn--primary" to={`/visita/${slug}`}>
                Soy nuevo
              </Link>
            </div>
            <Link className="btn btn--secondary" to={joinPath(site.id)}>
              Solicitar unirme
            </Link>
            <div className="church-actions__small">
              <ShareChurch url={`${pageUrl}/i/${slug}`} name={site.name} />
              {wa ? (
                <a className="church-action" href={wa} target="_blank" rel="noopener noreferrer">
                  <Icon name="chat" /> WhatsApp
                </a>
              ) : null}
              {site.mapsUrl || site.latitude != null ? (
                <a
                  className="church-action"
                  href={site.mapsUrl ?? `https://www.google.com/maps/dir/?api=1&destination=${site.latitude},${site.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="pin" /> Cómo llegar
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <div className="wall-body">
        <aside className="wall-side" aria-label="Información de la iglesia">
          <section className="wall-card" aria-labelledby="about-title">
            <h2 id="about-title">Quiénes somos</h2>
            {site.about ? <p className="pre-line">{site.about}</p> : null}
            <ul className="wall-facts">
              {site.pastorName ? <li><Icon name="user" /> Pastor: {site.pastorName}</li> : null}
              {site.denomination ? <li><Icon name="church" /> {site.denomination}</li> : null}
              {site.foundedYear ? <li><Icon name="calendar" /> Desde {site.foundedYear}</li> : null}
              {where ? <li><Icon name="pin" /> {where}</li> : null}
              {site.phone ? <li><Icon name="chat" /> <a href={`tel:${site.phone.replace(/[^\d+]/g, '')}`}>{site.phone}</a></li> : null}
              {site.email ? <li><Icon name="inbox" /> <a href={`mailto:${site.email}`}>{site.email}</a></li> : null}
              {site.helpLine ? <li><Icon name="hand" /> Línea de ayuda: {site.helpLine}</li> : null}
            </ul>
            {socials.length ? (
              <p className="social">
                {socials.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer">
                    {s.label}
                  </a>
                ))}
              </p>
            ) : null}
          </section>

          {site.schedules.length ? (
            <section className="wall-card" aria-labelledby="sched-title">
              <h2 id="sched-title">Horarios</h2>
              <ul className="wall-schedules">
                {site.schedules.map((s, i) => (
                  <li key={i}>
                    <strong>{s.label}</strong>
                    <span className="muted">
                      {s.day} · {s.time}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>

        <section className="wall-feed" aria-label="Publicaciones">
          {live && live.status !== 'off' ? (
            <div id="en-vivo">
              <Post site={site} what={live.status === 'live' ? 'está transmitiendo' : undefined} badge={<LiveBadge state={live} />}>
                <LivePlayer state={live} title={site.name} />
              </Post>
            </div>
          ) : null}

          {site.slides.length ? (
            <Post site={site} badge="Destacado">
              <Carousel slides={site.slides} />
            </Post>
          ) : null}

          {upcoming.map((e) => {
            const d = new Date(e.startsAt);
            return (
              <Post key={e.token} site={site} what={e.kind === 'meeting' ? 'publicó una reunión' : 'publicó un evento'} badge={e.kind === 'meeting' ? 'Próxima reunión' : 'Próximo evento'}>
                <Link to={e.kind === 'meeting' ? `/reunion/${e.token}` : `/e/${e.token}`} className="wall-event">
                  <span className="wall-event__date" aria-hidden="true">
                    <span>{d.toLocaleDateString('es', { month: 'short' }).replace('.', '')}</span>
                    <strong>{d.getDate()}</strong>
                  </span>
                  <span>
                    <strong>{e.title}</strong>
                    <span className="small muted">
                      {formatWhen(e.startsAt, e.endsAt)}
                      {e.placeName ? ` · ${e.placeName}` : ''}
                    </span>
                  </span>
                </Link>
              </Post>
            );
          })}

          {posts.length === 0 ? welcome : null}

          {posts.map((p) => {
            const thumb = youtubeThumb(p.videoUrl);
            const share = `https://wa.me/?text=${encodeURIComponent(`${p.title} · ${site.name}\n${pageUrl}/i/${slug}/${p.id}`)}`;
            return (
              <Post key={p.id} site={site} what={KIND_LABEL[p.kind] ?? 'publicó'} when={timeAgo(p.publishAt)}>
                <Link to={`/i/${slug}/${p.id}`} className="wall-post__title">
                  {p.title}
                </Link>
                {p.summary ? <p>{p.summary}</p> : null}
                {p.preacher || p.series ? (
                  <p className="small muted">{[p.preacher, p.series ? `Serie: ${p.series.title}` : null].filter(Boolean).join(' · ')}</p>
                ) : null}
                {thumb ? (
                  <Link to={`/i/${slug}/${p.id}`} className="wall-thumb" aria-label={`Ver video: ${p.title}`}>
                    <img src={thumb} alt="" loading="lazy" />
                    <span className="wall-thumb__play" aria-hidden="true">▶</span>
                  </Link>
                ) : null}
                <footer className="wall-post__foot">
                  <Link to={`/i/${slug}/${p.id}`}>{p.kind === 'sermon' ? (p.videoUrl ? 'Ver predicación' : 'Leer predicación') : 'Leer más'}</Link>
                  <a href={share} target="_blank" rel="noopener noreferrer">
                    Compartir
                  </a>
                </footer>
              </Post>
            );
          })}

          {posts.length ? welcome : null}
        </section>
      </div>
    </div>
  );
}
